import { useRef } from "react";
import { ArrowRight } from "lucide-react";
import { motion, useReducedMotion, useTransform } from "motion/react";
import { Tag, Body } from "../shared/Typography";
import { VioletBtn } from "../shared/Buttons";
import { ParticleOrb } from "../shared/ParticleOrb";
import { useHeroScrollFX } from "../../hooks/useHeroScrollFX";
import { useSplitHeadlineReveal } from "../../hooks/useSplitHeadlineReveal";
import { posthog } from "../../lib/posthog";

const EASE = [0.22, 1, 0.36, 1] as const;

export function CohartsHero({ onExploreIviz, onDiscoverForesight }: { onExploreIviz: () => void; onDiscoverForesight: () => void }) {
  const reduced = useReducedMotion();
  const { ref: scrollRef, scrollYProgress } = useHeroScrollFX();
  const headingRef = useRef<HTMLHeadingElement>(null);
  useSplitHeadlineReveal(headingRef);
  const orbY = useTransform(scrollYProgress, [0, 1], reduced ? [0, 0] : [0, 60]);
  const heroTextOpacity = useTransform(scrollYProgress, [0, 0.7, 1], reduced ? [1, 1, 1] : [1, 1, 0]);
  const heroTextY = useTransform(scrollYProgress, [0, 0.7, 1], reduced ? [0, 0, 0] : [0, 0, -30]);
  const container = { hidden: {}, show: { transition: { staggerChildren: reduced ? 0 : 0.12, delayChildren: 0.1 } } };
  const item = {
    hidden: reduced ? { opacity: 0 } : { opacity: 0, y: 24 },
    show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
  };

  return (
    <section ref={scrollRef} className="relative min-h-[100dvh] flex items-center overflow-hidden bg-[#FAFAFA] dark:bg-[#050505]">
      <motion.div className="absolute inset-0 z-0" style={{ y: orbY }}>
        <ParticleOrb variant="coharts-mark" accent="#4A90D9" lightAccent="#1E4FD8" offsetX={0} scrollProgress={scrollYProgress} />
      </motion.div>

      <div className="relative z-10 max-w-[1800px] mx-auto px-6 lg:px-10 pt-24 pb-16 w-full">
        <motion.div style={{ opacity: heroTextOpacity, y: heroTextY }}>
          <motion.div initial="hidden" animate="show" variants={container}>
            <motion.div variants={item}><Tag> Applied AI</Tag></motion.div>
            <h1
              ref={headingRef}
              className="font-display text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-medium leading-[1.02] tracking-[-0.02em] text-[#0A0A0F] dark:text-[#F5F5F7] text-left mt-6"
            >
              Intelligence<br />
              for <span className="text-[#1E4FD8] dark:text-[#4A90D9]">complex decisions</span>.
            </h1>
            <motion.div variants={item}>
              <Body className="text-xl font-normal text-[#0A0A0F]/80 dark:text-white/80 max-w-xl mt-6">
                Coharts builds AI software for complex decisions: iViz to analyze any data, with or
                without code, and Foresight to simulate clinical trials before patient one.
              </Body>
            </motion.div>
            <motion.div variants={item} className="flex flex-wrap gap-4 mt-8">
              <button
                onClick={() => {
                  posthog.capture("cta_clicked", { cta: "Explore iViz", location: "home_hero" });
                  onExploreIviz();
                }}
                className="inline-flex items-center gap-2 px-7 py-3.5 text-base font-medium rounded-full text-white dark:text-[#050505] bg-[#1E4FD8] dark:bg-[#4A90D9] transition-transform hover:scale-[1.02] active:scale-[0.98] active:brightness-90"
              >
                Explore iViz <ArrowRight size={16} />
              </button>
              <VioletBtn
                onClick={() => {
                  posthog.capture("cta_clicked", { cta: "Discover Foresight", location: "home_hero" });
                  onDiscoverForesight();
                }}
                large
              >
                Discover Foresight <ArrowRight size={16} />
              </VioletBtn>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
